import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../ui/dialog";
import {
  Link as LinkIcon, Copy, Trash2, XCircle, ShieldCheck, Eye, Calendar,
} from "lucide-react";
import { toast } from "sonner";
import { formatDate } from "../../lib/utils";

/**
 * Auditor share link management — lives inside the Documents tab.
 * Consultants create UUID+PIN links with an expiry, see access count +
 * last-accessed timestamp, revoke or delete links, and peek at the raw
 * access log.
 */
export default function AuditorShareCard({ leadId }) {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [logOpen, setLogOpen] = useState(null); // link
  const [newLink, setNewLink] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/share/leads/${leadId}`)
      .then((r) => setLinks(r.data))
      .catch(() => toast.error("Failed to load share links"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [leadId]);

  const publicUrl = (token) => `${window.location.origin}/audit/${token}`;

  function copyUrl(token) {
    navigator.clipboard.writeText(publicUrl(token));
    toast.success("Link copied");
  }

  async function revoke(l) {
    if (!window.confirm("Revoke this auditor link? The auditor will lose access immediately.")) return;
    try {
      await api.put(`/share/${l.id}/revoke`);
      toast.success("Link revoked");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  async function remove(l) {
    if (!window.confirm("Permanently delete this link and its access log?")) return;
    try {
      await api.delete(`/share/${l.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  return (
    <div className="p-4 border border-slate-200 rounded-md" data-testid="auditor-share-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-1 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5" />Auditor sharing
          </p>
          <p className="text-sm text-[#0D1B2A]">Secure PIN-protected links so the NDIS auditor can review the suite without a login.</p>
        </div>
        <Button size="sm" onClick={() => { setNewLink(null); setCreateOpen(true); }} className="bg-[#1A7F6E] hover:bg-[#136053] text-white" data-testid="create-share-link-btn">
          <LinkIcon className="w-3.5 h-3.5 mr-1.5" />New link
        </Button>
      </div>
      {loading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : links.length === 0 ? (
        <p className="text-xs text-slate-500">No auditor links yet. Click "New link" to create one.</p>
      ) : (
        <ul className="space-y-2">
          {links.map((l) => {
            const url = publicUrl(l.token);
            const state = l.revoked ? "Revoked" : (l.expires_at && new Date(l.expires_at) < new Date() ? "Expired" : "Active");
            const stateCls = state === "Active" ? "bg-[#E6F4F1] text-[#1A7F6E]" : "bg-rose-50 text-rose-700";
            return (
              <li key={l.id} className="border border-slate-200 rounded p-2.5 flex flex-col md:flex-row md:items-center gap-2" data-testid={`share-link-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge className={`text-[10px] ${stateCls}`}>{state}</Badge>
                    <code className="text-xs bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 text-[#0D1B2A] truncate max-w-[300px]" data-testid={`share-url-${l.id}`}>{url}</code>
                  </div>
                  <p className="text-[11px] text-slate-500 flex flex-wrap gap-x-2 items-center">
                    <Eye className="w-3 h-3" />
                    <span data-testid={`share-access-${l.id}`}>{l.access_count} view{l.access_count === 1 ? "" : "s"}</span>
                    {l.last_accessed_at && <span>· last {formatDate(l.last_accessed_at)}</span>}
                    <Calendar className="w-3 h-3 ml-1" />
                    <span>{l.expires_at ? `expires ${formatDate(l.expires_at)}` : "never expires"}</span>
                  </p>
                </div>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={() => copyUrl(l.token)} data-testid={`copy-share-${l.id}`}>
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setLogOpen(l)} data-testid={`log-share-${l.id}`}>
                    Log
                  </Button>
                  {!l.revoked && (
                    <Button size="sm" variant="outline" onClick={() => revoke(l)} className="text-amber-700" data-testid={`revoke-share-${l.id}`}>
                      <XCircle className="w-3.5 h-3.5" />
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => remove(l)} className="text-rose-600 hover:text-rose-700" data-testid={`delete-share-${l.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <CreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        leadId={leadId}
        onCreated={(data) => { setCreateOpen(false); setNewLink(data); load(); }}
      />

      {newLink && (
        <Dialog open onOpenChange={() => setNewLink(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader><DialogTitle className="text-[#0D1B2A]">Share this link with the auditor</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="p-3 bg-[#E6F4F1] rounded space-y-2">
                <p className="text-[10px] uppercase tracking-widest text-[#1A7F6E]">Auditor URL</p>
                <div className="flex gap-2 items-center">
                  <code className="flex-1 text-xs bg-white border border-slate-200 rounded px-2 py-1.5 truncate">{publicUrl(newLink.token)}</code>
                  <Button size="sm" variant="outline" onClick={() => copyUrl(newLink.token)}><Copy className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Send the PIN <b>separately</b> (SMS or verbal) so an intercepted link is useless.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setNewLink(null)}>Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {logOpen && (
        <AccessLogDialog link={logOpen} onClose={() => setLogOpen(null)} />
      )}
    </div>
  );
}

function CreateDialog({ open, onOpenChange, leadId, onCreated }) {
  const [pin, setPin] = useState("");
  const [ttl, setTtl] = useState("30");
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (pin.trim().length < 4) return toast.error("PIN must be at least 4 characters");
    setSaving(true);
    try {
      const r = await api.post(`/share/leads/${leadId}`, {
        pin: pin.trim(),
        expires_in_days: parseInt(ttl, 10) || 0,
      });
      toast.success("Auditor link created");
      onCreated(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setSaving(false);
    setPin("");
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="create-share-dialog">
        <DialogHeader><DialogTitle className="text-[#0D1B2A]">New auditor link</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>PIN (share separately with auditor)</Label>
            <Input value={pin} onChange={(e) => setPin(e.target.value)} placeholder="e.g. 4831" data-testid="share-pin-input" />
            <p className="text-[11px] text-slate-500 mt-1">Min 4 characters. Stored hashed on the server.</p>
          </div>
          <div>
            <Label>Expires in</Label>
            <Select value={ttl} onValueChange={setTtl}>
              <SelectTrigger data-testid="share-ttl-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">7 days</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="0">Never</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-[#1A7F6E] hover:bg-[#136053] text-white" data-testid="submit-create-share">
            {saving ? "Creating…" : "Create link"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AccessLogDialog({ link, onClose }) {
  const [entries, setEntries] = useState(null);
  useEffect(() => {
    api.get(`/share/${link.id}/access-log`)
      .then((r) => setEntries(r.data))
      .catch(() => setEntries([]));
  }, [link]);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="access-log-dialog">
        <DialogHeader><DialogTitle className="text-[#0D1B2A]">Access log · {link.access_count} views</DialogTitle></DialogHeader>
        <div className="max-h-[400px] overflow-y-auto">
          {entries === null ? (
            <p className="text-xs text-slate-500">Loading…</p>
          ) : entries.length === 0 ? (
            <p className="text-xs text-slate-500">No accesses yet.</p>
          ) : (
            <ul className="space-y-1 text-xs">
              {entries.map((e) => (
                <li key={e.id} className="border-b border-slate-100 py-1.5" data-testid={`log-entry-${e.id}`}>
                  <span className="text-slate-500">{new Date(e.at).toLocaleString()}</span>
                  {" · "}<code className="text-[10px] text-slate-400">{e.ip_hash.slice(0, 12)}…</code>
                  {e.user_agent && <p className="text-[10px] text-slate-400 truncate">{e.user_agent}</p>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
